import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './AdminModals.module.css';
import detailStyles from './DetailPage.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { companiesAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

export const CompanyEditPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { companies, refreshData } = useAdmin();

  const company = companies.find((c) => c._id === companyId);
  const isNew = !companyId;

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    address: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    logo: '',
    subscription: { plan: 'basic', status: 'active' },
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        domain: company.domain || '',
        address: typeof company.address === 'object' ? '' : company.address || '',
        contactPerson: company.contactPerson || '',
        contactEmail: company.contactEmail || company.email || '',
        contactPhone: company.contactPhone || '',
        logo: company.logo || '',
        subscription: { plan: company.subscription?.plan || 'basic', status: company.subscription?.status || 'active' },
      });
      if (company.logo) {
        setLogoPreview(company.logo);
      }
    }
  }, [company]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning('Logo te groot', 'Max 5MB.');
        return;
      }
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.warning('Ongeldig formaat', 'Upload JPEG, PNG, GIF, SVG of WebP.');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({ ...formData, logo: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { logo, ...data } = formData;

      if (isNew) {
        const created = await companiesAPI.create(data);
        const createdId = created._id || created.id;
        if (logoFile && createdId) {
          try { await companiesAPI.uploadLogo(createdId, logoFile); } catch {}
        }
        toast.success('Klant aangemaakt');
        refreshData();
        navigate(`/admin/companies/${createdId}`);
      } else {
        await companiesAPI.update(companyId!, data);
        if (logoFile) {
          try { await companiesAPI.uploadLogo(companyId!, logoFile); } catch {}
        }
        toast.success('Klant bijgewerkt');
        refreshData();
        navigate(`/admin/companies/${companyId}`);
      }
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setSaving(false);
    }
  };

  if (companyId && !company) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#9DA6A8', marginBottom: '1rem' }}>Klant niet gevonden.</p>
        <Button onClick={() => navigate('/admin/companies')}>Terug naar klanten</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <button onClick={() => navigate(companyId ? `/admin/companies/${companyId}` : '/admin/companies')} className={detailStyles.backLink}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        {companyId ? 'Terug naar klant' : 'Terug naar klanten'}
      </button>

      <h1 style={{ fontFamily: "'Avenir Next', sans-serif", fontSize: '1.75rem', fontWeight: 600, color: '#323232', margin: '0 0 1.5rem' }}>
        {isNew ? 'Nieuwe klant' : `${company?.name} bewerken`}
      </h1>

      <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #EEEEEE', padding: '2rem' }}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Bedrijfsnaam *</label>
            <input type="text" id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>

          <div className={styles.formGroup}>
            <label>Logo</label>
            <div className={styles.logoUploadContainer}>
              {logoPreview ? (
                <div className={styles.logoPreview}>
                  <img src={logoPreview} alt="Logo preview" />
                  <button type="button" onClick={handleRemoveLogo} className={styles.removeLogoBtn} title="Verwijder">×</button>
                </div>
              ) : (
                <div className={styles.logoPlaceholder}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>Geen logo</span>
                </div>
              )}
              <input type="file" id="logo" accept="image/*" onChange={handleLogoChange} className={styles.fileInput} />
              <label htmlFor="logo" className={styles.uploadButton}>Kies logo</label>
              <small>Max 5MB. JPEG, PNG, GIF, SVG, WebP</small>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="domain">Domein</label>
            <input type="text" id="domain" value={formData.domain} onChange={(e) => setFormData({ ...formData, domain: e.target.value })} placeholder="voorbeeld.nl" />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="address">Adres</label>
            <textarea id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} rows={2} />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="contactPerson">Contactpersoon</label>
              <input type="text" id="contactPerson" value={formData.contactPerson} onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="contactPhone">Telefoon</label>
              <input type="tel" id="contactPhone" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="contactEmail">E-mail</label>
            <input type="email" id="contactEmail" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="subscriptionPlan">Abonnement</label>
              <select id="subscriptionPlan" value={formData.subscription.plan} onChange={(e) => setFormData({ ...formData, subscription: { ...formData.subscription, plan: e.target.value } })}>
                <option value="basic">Basic</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="subscriptionStatus">Status abonnement</label>
              <select id="subscriptionStatus" value={formData.subscription.status} onChange={(e) => setFormData({ ...formData, subscription: { ...formData.subscription, status: e.target.value } })}>
                <option value="active">Actief</option>
                <option value="trial">Proefperiode</option>
                <option value="suspended">Opgeschort</option>
                <option value="expired">Verlopen</option>
              </select>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={() => navigate(companyId ? `/admin/companies/${companyId}` : '/admin/companies')}>
              Annuleren
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Opslaan...' : (isNew ? 'Klant aanmaken' : 'Opslaan')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
